import React, { useState, useEffect, useRef } from 'react';

/* ── Scroll-triggered fade-in ───────────────────────────────────────────── */
export function FadeIn({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 0.7s ${delay}s cubic-bezier(0.4,0,0.2,1), transform 0.7s ${delay}s cubic-bezier(0.4,0,0.2,1)`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ── Section title with mono subtitle ───────────────────────────────────── */
export function SectionTitle({ subtitle, title }) {
  return (
    <div style={{ marginBottom: 44 }}>
      <p style={{
        fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
        color: 'var(--cyan)', textTransform: 'uppercase',
        letterSpacing: '0.18em', marginBottom: 14,
      }}>
        {subtitle}
      </p>
      <h2 style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'clamp(1.9rem, 3.8vw, 2.8rem)',
        fontWeight: 700, color: 'var(--text-primary)',
        lineHeight: 1.15, letterSpacing: '-0.015em',
      }}>
        {title}
      </h2>
    </div>
  );
}

/* ── Button ─────────────────────────────────────────────────────────────── */
export function Btn({ children, onClick, outline, large, type = 'button' }) {
  const base = {
    border: 'none', borderRadius: 6, cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontSize: large ? '0.95rem' : '0.88rem',
    fontWeight: 600,
    transition: 'all 0.3s',
    padding: large ? '15px 36px' : '12px 26px',
  };

  if (outline) {
    return (
      <button type={type} onClick={onClick} style={{
        ...base, background: 'transparent',
        border: '1px solid rgba(255,255,255,0.18)', color: '#fff',
      }}
        onMouseEnter={e => { e.target.style.borderColor = 'var(--cyan)'; e.target.style.color = 'var(--cyan)'; }}
        onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,0.18)'; e.target.style.color = '#fff'; }}
      >
        {children}
      </button>
    );
  }

  return (
    <button type={type} onClick={onClick} style={{
      ...base,
      background: 'linear-gradient(135deg, var(--blue), var(--cyan))',
      color: '#fff',
      boxShadow: '0 4px 20px rgba(33,150,243,0.25)',
    }}
      onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 30px rgba(33,150,243,0.35)'; }}
      onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 20px rgba(33,150,243,0.25)'; }}
    >
      {children}
    </button>
  );
}

/* ── Card ───────────────────────────────────────────────────────────────── */
export function Card({ children, style = {} }) {
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 12, padding: 28,
        transition: 'border-color 0.3s, background 0.3s',
        ...style,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--card-hover-border)';
        e.currentTarget.style.background = 'var(--card-hover-bg)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--card-border)';
        e.currentTarget.style.background = 'var(--card-bg)';
      }}
    >
      {children}
    </div>
  );
}

/* ── Tag pill ───────────────────────────────────────────────────────────── */
export function Tag({ children, blue }) {
  return (
    <span style={{
      fontFamily: 'var(--font-body)', fontSize: '0.72rem', fontWeight: 500,
      color: blue ? 'var(--blue)' : 'var(--cyan)',
      background: blue ? 'rgba(33,150,243,0.08)' : 'rgba(41,182,246,0.08)',
      border: `1px solid ${blue ? 'rgba(33,150,243,0.15)' : 'rgba(41,182,246,0.15)'}`,
      borderRadius: 4, padding: '4px 12px', letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
